import React, { useEffect, useState } from "react";
import { Badge, Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import EmployeeDataService from "../../services/Employee";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { HrmsStatCard } from "../../components/Hrms/HrmsStatCard";
import { getUserAccess } from "../../utils/authUtils";

interface PendingApproval {
  _id: string;
  name?: string;
  email: string;
  type: string;
  start_date: string;
  end_date: string;
}

interface TeamLeaveRequest {
  _id: string;
  name?: string;
  email: string;
  type: string;
  status: string;
  start_date: string;
  end_date: string;
}

interface TodayAttendance {
  _id?: string;
  email?: string;
  name?: string;
  loginTime?: string | null;
}

const COLORS = ["#308888", "#e2e8f0"];

export const ManagerDashboard = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";

  const [isLoading, setIsLoading] = useState(true);
  const [teamSize, setTeamSize] = useState(0);
  const [presentToday, setPresentToday] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>(
    [],
  );
  const [teamLeaveRequests, setTeamLeaveRequests] = useState<
    TeamLeaveRequest[]
  >([]);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance[]>([]);
  const { canViewManagerDashboard } = getUserAccess();

  const fetchWidgets = async () => {
    if (!canViewManagerDashboard) return;
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getManagerDashboardWidgets(
        token,
        userEmail,
      );
      if (res.data.status === "success" && res.data.data) {
        setTeamSize(res.data.data.teamSize || 0);
        setPresentToday(res.data.data.presentToday || 0);
        setPendingApprovals(res.data.data.pendingApprovals || []);
        setTeamLeaveRequests(res.data.data.teamLeaveRequests || []);
        setTodayAttendance(res.data.data.todayAttendance || []);
      }
    } catch (e: any) {
      console.error("Error fetching manager dashboard widgets:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWidgets();
  }, []);

  const presentEmployees = todayAttendance.filter(
    (employee) =>
      typeof employee.loginTime === "string" &&
      employee.loginTime.trim().length > 0,
  );

  const getInitials = (name?: string) =>
    (name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase() || "?";

  const attendanceData = [
    { name: "Present", value: presentToday },
    { name: "Absent", value: Math.max(0, teamSize - presentToday) },
  ];

  return (
    <HrmsPageShell
      title="Manager Dashboard"
      sectionKey="dashboards"
      noPermission={!canViewManagerDashboard}
      isLoading={canViewManagerDashboard && isLoading}
      isEmpty={canViewManagerDashboard && !isLoading && teamSize === 0}
      emptyText="You don't currently have any direct reports."
    >
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <HrmsStatCard label="Team Size" value={teamSize} />
        <HrmsStatCard
          label="Present Today"
          value={`${presentToday} / ${teamSize}`}
          accent="#2e7d32"
        />
        <HrmsStatCard
          label="Pending Leave Approvals"
          value={pendingApprovals.length}
          accent="#fdb508"
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <HrmsCard title="Today's Team Attendance">
          <Flex
            direction={{ base: "column", lg: "row" }}
            align="stretch"
            gap={{ base: 4, lg: 5 }}
          >
            {/* Attendance chart */}
            <Box
              position="relative"
              w={{ base: "100%", lg: "190px" }}
              flexShrink={0}
            >
              <Box h="190px" position="relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={attendanceData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={78}
                      stroke="none"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {attendanceData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        fontSize: "12px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <Flex
                  position="absolute"
                  inset={0}
                  direction="column"
                  align="center"
                  justify="center"
                  pointerEvents="none"
                >
                  <Text
                    fontSize="28px"
                    fontWeight="700"
                    color="#308888"
                    lineHeight="1"
                    mb={1}
                  >
                    {presentToday}
                  </Text>

                  <Text fontSize="11px" color="gray.500" mb={0}>
                    of {teamSize} present
                  </Text>
                </Flex>
              </Box>

              <Flex justify="center" gap={4}>
                {attendanceData.map((entry, index) => (
                  <Flex key={entry.name} align="center" gap={2}>
                    <Box boxSize="8px" borderRadius="full" bg={COLORS[index]} />

                    <Text fontSize="11px" color="gray.500" mb={0}>
                      {entry.name} ({entry.value})
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Box>

      
            <Box
              flex={1}
              minW={0}
              borderLeft={{ base: "none", lg: "1px solid #eef2f2" }}
              borderTop={{ base: "1px solid #eef2f2", lg: "none" }}
              pl={{ base: 0, lg: 5 }}
              pt={{ base: 4, lg: 2 }}
            >
              <Flex align="center" justify="space-between" gap={2} mb={3}>
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  letterSpacing="0.6px"
                  color="gray.600"
                  mb={0}
                >
                  PRESENT TODAY
                </Text>

                <Badge
                  bg="#e8f4f3"
                  color="#308888"
                  borderRadius="full"
                  px={2}
                  py="2px"
                  fontSize="10px"
                  whiteSpace="nowrap"
                >
                  {presentEmployees.length} members
                </Badge>
              </Flex>

              {presentEmployees.length > 0 ? (
                <Box
                  as="ul"
                  aria-label="Employees present today"
                  m={0}
                  p={0}
                  listStyleType="none"
                  maxH="190px"
                  overflowY="auto"
                  pr={2}
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c9dedd",
                      borderRadius: "8px",
                    },
                  }}
                >
                  {presentEmployees.map((employee, index) => (
                    <Flex
                      as="li"
                      key={employee._id || employee.email || index}
                      align="center"
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius="8px"
                      _hover={{ bg: "#f4f9f9" }}
                    >
                      <Flex
                        boxSize="32px"
                        flexShrink={0}
                        align="center"
                        justify="center"
                        borderRadius="full"
                        bg="#e8f4f3"
                        color="#308888"
                        fontSize="11px"
                        fontWeight="700"
                        aria-hidden="true"
                      >
                        {getInitials(employee.name)}
                      </Flex>

                      <Text
                        flex={1}
                        minW={0}
                        fontSize="12px"
                        fontWeight="500"
                        color="gray.700"
                        overflowWrap="anywhere"
                        mb={0}
                      >
                        {employee.name?.trim() || "Name unavailable"}
                      </Text>

                      <Box
                        boxSize="7px"
                        flexShrink={0}
                        borderRadius="full"
                        bg="#308888"
                        aria-hidden="true"
                      />
                    </Flex>
                  ))}
                </Box>
              ) : (
                <Flex
                  minH="140px"
                  align="center"
                  justify="center"
                  bg="#f8fafa"
                  borderRadius="10px"
                  px={4}
                >
                  <Text
                    fontSize="12px"
                    color="gray.500"
                    textAlign="center"
                    mb={0}
                  >
                    No team members have logged in today.
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>
        </HrmsCard>

        <HrmsCard title="Pending Leave Approvals">
          {pendingApprovals.length > 0 ? (
            pendingApprovals.map((leave) => (
              <Flex
                key={leave._id}
                justify="space-between"
                py={2}
                borderBottom="1px solid #eef2f2"
              >
                <Text fontSize="13px" mb={0}>
                  {leave.name || leave.email} — {leave.type}
                </Text>
                <Badge colorScheme="orange">
                  {leave.start_date} to {leave.end_date}
                </Badge>
              </Flex>
            ))
          ) : (
            <Text fontSize="13px" color="gray.500" mb={0}>
              No pending approvals.
            </Text>
          )}
        </HrmsCard>
      </SimpleGrid>

      <Box mt={4}>
        <HrmsCard title="Team Leave Requests">
          {teamLeaveRequests.length > 0 ? (
            teamLeaveRequests.map((leave) => (
              <Flex
                key={leave._id}
                justify="space-between"
                py={2}
                borderBottom="1px solid #eef2f2"
              >
                <Text fontSize="13px" mb={0}>
                  {leave.name || leave.email} — {leave.type}
                </Text>
                <Flex align="center" gap={2}>
                  <Text fontSize="13px" color="gray.500" mb={0}>
                    {leave.start_date} to {leave.end_date}
                  </Text>
                  <Badge
                    colorScheme={
                      leave.status === "approved"
                        ? "green"
                        : leave.status === "declined"
                          ? "red"
                          : "orange"
                    }
                  >
                    {leave.status}
                  </Badge>
                </Flex>
              </Flex>
            ))
          ) : (
            <Text fontSize="13px" color="gray.500" mb={0}>
              No leave requests from your team yet.
            </Text>
          )}
        </HrmsCard>
      </Box>
    </HrmsPageShell>
  );
};
